import React from "react"
import { connect } from "react-redux"

import { CampaignType, DISPLAY_EARNING_RULE_REWARD_TYPE as DISPLAY_REWARD_TYPE, EarningRuleRewardType, EarningRuleType } from "config/CustomEnums"
import SinglePreviewCard from "./SinglePreviewCard"
import { getReadablePeriod } from "utils/TimeFormatUtil"
import { DISPLAY_REWARD_LOGIC_TYPE, REWARD_LOGIC_TYPE } from "components/earning/RewardLogicSelector"
import { DISPLAY_REWARD_AMOUNT_BASED_TYPE, REWARD_AMOUNT_TYPE } from "components/earning/RewardAmountInputArea"
import { EARNING_RULE_PERIODIC_TYPE } from "components/earning/PurchaseEarningRuleRewardLimitInputs"
import { LinkSection } from "components/base/LinkSection"
import { CAMPAIGN_EARNING_RULE_PAGE_ANCHOR_KEY } from "config/PageAnchorConfig/CampaignAnchorConfig"


export const getDisplayRewardAmount = (earningRule) => {
  let displayRewardAmount = ""
  if (
    earningRule?.rewardAmountType === REWARD_AMOUNT_TYPE.oneOff
  ) {
    displayRewardAmount += "One-off fixed amount"
  } else if (
    earningRule?.rewardAmountType === REWARD_AMOUNT_TYPE.dynamic
  ) {
    displayRewardAmount += "Dynamic"
  }
  if (
    earningRule?.rewardAmountType === REWARD_AMOUNT_TYPE.dynamic
    && earningRule?.lineItemRequirement
    && earningRule?.rewardAmountBasedType
  ) {
    displayRewardAmount += `\n${DISPLAY_REWARD_AMOUNT_BASED_TYPE?.[earningRule?.rewardAmountBasedType]}`
  }
  if (earningRule?.rewardAmountType === REWARD_AMOUNT_TYPE.oneOff) {
    if (earningRule.rewardType === EarningRuleRewardType.stamps) {
      displayRewardAmount += `\n${earningRule?.stampTypeRewardPerXValue || '-'} stamps`
      return displayRewardAmount
    }
    if (earningRule.rewardType === EarningRuleRewardType.coupons) {
      displayRewardAmount += `\n${earningRule?.couponTypeRewardPerXValue || '-'} coupons`
      return displayRewardAmount
    }
    if (earningRule.rewardType === EarningRuleRewardType.points) {
      displayRewardAmount += `\n${earningRule?.pointTypeRewardPerXValue || '-'} pts; ${earningRule?.tpeTypeRewardPerXValue || '-'} pp`
      return displayRewardAmount
    }
  }
  if (earningRule?.rewardAmountType === REWARD_AMOUNT_TYPE.dynamic) {
    const per = earningRule?.rewardLogicType === REWARD_LOGIC_TYPE.baseOnItemQuantity ? "Every" : "HK$";
    const unit = earningRule?.rewardLogicType === REWARD_LOGIC_TYPE.baseOnItemQuantity ? "number of item =" : "="

    if (earningRule.rewardType === EarningRuleRewardType.stamps) {
      displayRewardAmount += `\n${per} ${earningRule?.stampTypeRewardPerX || '-'} ${unit} ${earningRule?.stampTypeRewardPerXValue || '-'} stamps`
      return displayRewardAmount
    }
    if (earningRule.rewardType === EarningRuleRewardType.coupons) {
      displayRewardAmount += `\n${per} ${earningRule?.couponTypeRewardPerX || '-'} ${unit} ${earningRule?.couponTypeRewardPerXValue || '-'} coupons`
      return displayRewardAmount
    }
    if (earningRule.rewardType === EarningRuleRewardType.points) {
      displayRewardAmount += `\n${per} ${earningRule?.pointTypeRewardPerX || '-'} ${unit} ${earningRule?.pointTypeRewardPerXValue || '-'} pts`
      displayRewardAmount += `\n${per} ${earningRule?.tpeTypeRewardPerX || '-'} ${unit} ${earningRule?.tpeTypeRewardPerXValue || '-'} pp`
      return displayRewardAmount
    }
  }
  return displayRewardAmount
};

const getDisplayLevelGoal = (levelSource, levelGoal) => {
  if (levelGoal?.label) {
    return levelGoal?.label;
  };
  if (levelSource) {
    let fromLevel = levelSource.find(
      (level) => level.levelRank === levelGoal?.levelRank - 1,
    );
    return fromLevel?.levelName + ' to ' + levelGoal?.levelName;
  }
  return '-';
};

const EarningRuleCard = (earningRule) => {
  const earningRuleType = earningRule.type;
  const rewardType = earningRule.rewardType;

  let requirements = [];

  let rewards = [
    {
      isMandatory: true,
      title: "Reward type",
      field: "linkedEarningRules.rewardType",
      step: 0,
      anchorKey: "reward_type_key",
    },
    {
      isMandatory: true,
      title: "Quantity",
      field: "linkedEarningRules.quantity",
      anchorKey: CAMPAIGN_EARNING_RULE_PAGE_ANCHOR_KEY.reward.sectionContent.reward_quantity_key,
    },
    {
      isMandatory: true,
      title: "Progression Points",
      field: "linkedEarningRules.tpeQuantity",
      errorField: 'linkedEarningRules.pointReward',
      anchorKey: CAMPAIGN_EARNING_RULE_PAGE_ANCHOR_KEY.reward.sectionContent.reward_tpe_key,
    },
  ];

  let limits = [
    {
      title: "Overall Limit (optional)",
      field: "linkedEarningRules.overallLimit",
      anchorKey: CAMPAIGN_EARNING_RULE_PAGE_ANCHOR_KEY.limit.sectionContent.limit_section_key,
    },
    {
      title: "Periodic Limit (optional)",
      field: "displayPeriodicLimit",
      anchorKey: CAMPAIGN_EARNING_RULE_PAGE_ANCHOR_KEY.limit.sectionContent.limit_section_key,
    },
    {
      title: "Per head Limit (optional)",
      field: "linkedEarningRules.perHeadLimit",
      anchorKey: CAMPAIGN_EARNING_RULE_PAGE_ANCHOR_KEY.limit.sectionContent.limit_section_key,
    },
    {
      title: "Per head periodic Limit (optional)",
      field: "displayPerHeadPeriodicLimit",
      anchorKey: CAMPAIGN_EARNING_RULE_PAGE_ANCHOR_KEY.limit.sectionContent.limit_section_key,
    },
  ];

  if (rewardType === EarningRuleRewardType.coupons) {
    rewards = [
      {
        isMandatory: true,
        title: "Reward type",
        field: "linkedEarningRules.rewardType",
        step: 0,
        anchorKey: "reward_type_key",
      },
      {
        isMandatory: true,
        title: "Link to coupon set",
        field: "linkedEarningRules.coupons",
        step: 0,
        anchorKey: "have_created_coupon_key",
        customRender: (
          <div className='d-flex'>
            <label className="custom-record-normal-font custom-timeline-time-color list-new-line">
              {`[ID: ${earningRule?.coupons?.pk}] ${earningRule?.coupons?.name}`}
            </label>
            <LinkSection
              link={`/coupons/${earningRule?.coupons?.pk}`}
              linkText={'View coupon set'}
              disableLink={earningRule?.coupons?.toDelete}
              disableLinkText="Deleted"
            />
          </div>
        )
      },
      {
        isMandatory: true,
        title: "Quantity",
        field: "linkedEarningRules.quantity",
        anchorKey: CAMPAIGN_EARNING_RULE_PAGE_ANCHOR_KEY.reward.sectionContent.reward_quantity_key,
      },
    ]
  }

  switch (earningRuleType) {
    case EarningRuleType.generalPurchase:
      requirements = [
        {
          title: "Minimum spending per transaction",
          field: "linkedEarningRules.minSpending",
          anchorKey: CAMPAIGN_EARNING_RULE_PAGE_ANCHOR_KEY.requirements.sectionContent.per_transaction_key,
        },
        {
          title: "Minimum quantity per transaction",
          field: "linkedEarningRules.minQuantity",
          anchorKey: CAMPAIGN_EARNING_RULE_PAGE_ANCHOR_KEY.requirements.sectionContent.per_transaction_key,
        },
        {
          title: "Accept receipt X days after purchase for system handling (optional)",
          field: "linkedEarningRules.eligibleDays",
          anchorKey: CAMPAIGN_EARNING_RULE_PAGE_ANCHOR_KEY.requirements.sectionContent.eligible_days_key,
        },
        {
          title: "Upload receipt X days after purchase (optional)",
          field: "linkedEarningRules.uploadEligibleDays",
          anchorKey: CAMPAIGN_EARNING_RULE_PAGE_ANCHOR_KEY.requirements.sectionContent.upload_eligible_days_key,
        },
        {
          isBoolean: true,
          title: "Exclude delivery cost (for online purchase)",
          field: "linkedEarningRules.isExcludeDeliveryCost",
          anchorKey: CAMPAIGN_EARNING_RULE_PAGE_ANCHOR_KEY.requirements.sectionContent.is_exclude_delivery_cost_key,
        },
        {
          isBoolean: true,
          title: "Exclude other charges (for online purchase)",
          field: "linkedEarningRules.isExcludeOtherCharges",
          anchorKey: CAMPAIGN_EARNING_RULE_PAGE_ANCHOR_KEY.requirements.sectionContent.is_exclude_other_charges_key,
        },
        {
          isMandatory: true,
          isBoolean: true,
          title: "Line item requirement (optional)",
          field: "linkedEarningRules.lineItemRequirement",
          anchorKey: CAMPAIGN_EARNING_RULE_PAGE_ANCHOR_KEY.requirements.sectionContent.line_item_requirement_key,
        },
        {
          isMandatory: true,
          title: "Line item requirement (optional)",
          field: "linkedEarningRules.lineItemGroups",
          anchorKey: CAMPAIGN_EARNING_RULE_PAGE_ANCHOR_KEY.requirements.sectionContent.line_item_requirement_key,
        },
        {
          isMandatory: true,
          title: "Eligible stores",
          field: "readableSelectedStores",
          anchorKey: CAMPAIGN_EARNING_RULE_PAGE_ANCHOR_KEY.requirements.sectionContent.have_eligible_stores_key,
        },
      ];
      rewards = [
        {
          isMandatory: true,
          title: "Reward logic",
          field: "displayRewardLogicType",
          anchorKey: CAMPAIGN_EARNING_RULE_PAGE_ANCHOR_KEY.reward.sectionContent.reward_logic_section_key,
        },
        {
          isMandatory: true,
          title: "Reward type",
          field: "displayRewardType",
          step: 0,
          anchorKey: "reward_type_key",
        },
        {
          isMandatory: true,
          title: "Reward amount",
          field: "displayRewardAmount",
          anchorKey: CAMPAIGN_EARNING_RULE_PAGE_ANCHOR_KEY.reward.sectionContent.reward_amount_input_area_key,
        },
      ];
      limits = [
        { title: 'Maximum amount of rewards this earning rule can be issued in all time', field: 'displayEarningRuleAllTimeMaxAmount', anchorKey: CAMPAIGN_EARNING_RULE_PAGE_ANCHOR_KEY.limit.sectionContent.purchase_earning_rule_reward_limit_key },
        { title: 'Maximum number of times this earning rule can be triggered in all time', field: 'linkedEarningRules.earningRuleAllTimeLimitMaxTriggerTimes', anchorKey: CAMPAIGN_EARNING_RULE_PAGE_ANCHOR_KEY.limit.sectionContent.purchase_earning_rule_reward_limit_key },
        { title: `[Earning rule]Time period - per ${earningRule?.earningRuleTimePeriodLimitPeriodicTypePerDays || "X"} days`, field: 'displayEarningRuleTimePeriodPerXDay', anchorKey: CAMPAIGN_EARNING_RULE_PAGE_ANCHOR_KEY.limit.sectionContent.purchase_earning_rule_reward_limit_key },
        { title: '[Earning rule]Time period - Specific start and end time', field: 'displayEarningRuleTimePeriodSpecialPeriod', anchorKey: CAMPAIGN_EARNING_RULE_PAGE_ANCHOR_KEY.limit.sectionContent.purchase_earning_rule_reward_limit_key },
        { title: '[Earning rule]Time period follow active period', field: 'displayEarningRuleTimePeriodFollowActivePeriod', anchorKey: CAMPAIGN_EARNING_RULE_PAGE_ANCHOR_KEY.limit.sectionContent.purchase_earning_rule_reward_limit_key },
        { title: '[Earning rule]Customised time period', field: 'displayEarningRuleCustomisedTimePeriod', anchorKey: CAMPAIGN_EARNING_RULE_PAGE_ANCHOR_KEY.limit.sectionContent.purchase_earning_rule_reward_limit_key },
        { title: 'Maximum amount of rewards this earning rule can be issued within a specified time period', field: 'displayEarningRuleSpecialTimeMaxAmount', anchorKey: CAMPAIGN_EARNING_RULE_PAGE_ANCHOR_KEY.limit.sectionContent.purchase_earning_rule_reward_limit_key },
        { title: 'Maximum number of times this earning rule can be triggered within a specified time period', field: 'linkedEarningRules.earningRuleTimePeriodLimitMaxTriggerTimes', anchorKey: CAMPAIGN_EARNING_RULE_PAGE_ANCHOR_KEY.limit.sectionContent.purchase_earning_rule_reward_limit_key },
        { title: 'Maximum amount of rewards this earning rule can be issued per member in all time', field: 'displayPerMemberAllTimeMaxAmount', anchorKey: CAMPAIGN_EARNING_RULE_PAGE_ANCHOR_KEY.limit.sectionContent.purchase_earning_rule_reward_limit_key },
        { title: 'Maximum number of times this earning rule can be triggered per member in all time', field: 'linkedEarningRules.perMemberAllTimeLimitMaxTriggerTimes', anchorKey: CAMPAIGN_EARNING_RULE_PAGE_ANCHOR_KEY.limit.sectionContent.purchase_earning_rule_reward_limit_key },
        { title: `[Per member]Time period - per ${earningRule?.perMemberTimePeriodLimitPeriodicTypePerDays || "X"} days`, field: 'displayPerMemberTimePeriodPerXDay', anchorKey: CAMPAIGN_EARNING_RULE_PAGE_ANCHOR_KEY.limit.sectionContent.purchase_earning_rule_reward_limit_key },
        { title: '[Per member]Time period - Specific start and end time', field: 'displayPerMemberTimePeriodSpecialPeriod', anchorKey: CAMPAIGN_EARNING_RULE_PAGE_ANCHOR_KEY.limit.sectionContent.purchase_earning_rule_reward_limit_key },
        { title: '[Per member]Time period follow active period', field: 'displayPerMemberTimePeriodFollowActivePeriod', anchorKey: CAMPAIGN_EARNING_RULE_PAGE_ANCHOR_KEY.limit.sectionContent.purchase_earning_rule_reward_limit_key },
        { title: '[Per member]Customised time period', field: 'displayPerMemberCustomisedTimePeriod', anchorKey: CAMPAIGN_EARNING_RULE_PAGE_ANCHOR_KEY.limit.sectionContent.purchase_earning_rule_reward_limit_key },
        { title: 'Maximum amount of rewards this earning rule can be issued per member within a specified time period', field: 'displayPerMemberSpecialTimeMaxAmount', anchorKey: CAMPAIGN_EARNING_RULE_PAGE_ANCHOR_KEY.limit.sectionContent.purchase_earning_rule_reward_limit_key },
        { title: 'Maximum number of times this earning rule can be triggered per member within a specified time period', field: 'linkedEarningRules.perMemberTimePeriodLimitMaxTriggerTimes', anchorKey: CAMPAIGN_EARNING_RULE_PAGE_ANCHOR_KEY.limit.sectionContent.purchase_earning_rule_reward_limit_key },
        { title: 'Maximum amount of rewards this earning rule can be issued per transaction', field: 'displayPerTransactionMaxAmount', anchorKey: CAMPAIGN_EARNING_RULE_PAGE_ANCHOR_KEY.limit.sectionContent.purchase_earning_rule_reward_limit_key },
      ];
      break;
    case EarningRuleType.qrCodeScanning:
      requirements = [
        {
          isMandatory: true,
          title: "QR codes",
          field: "linkedEarningRules.qrCodes",
          anchorKey: CAMPAIGN_EARNING_RULE_PAGE_ANCHOR_KEY.requirements.sectionContent.qr_code_scanning_section_key,
        }
      ];
      break
    case EarningRuleType.gpsCheckIn:
      requirements = [
        {
          isMandatory: true,
          title: "Latitude",
          field: "linkedEarningRules.latitude",
          anchorKey: CAMPAIGN_EARNING_RULE_PAGE_ANCHOR_KEY.requirements.sectionContent.gps_latitude_key,
        },
        {
          isMandatory: true,
          title: "Longitude",
          field: "linkedEarningRules.longitude",
          anchorKey: CAMPAIGN_EARNING_RULE_PAGE_ANCHOR_KEY.requirements.sectionContent.gps_longitude_key,
        },
        {
          isMandatory: true,
          title: "Radius",
          field: "linkedEarningRules.radius",
          anchorKey: CAMPAIGN_EARNING_RULE_PAGE_ANCHOR_KEY.requirements.sectionContent.gps_radius_key,
        }
      ];
      break
    case EarningRuleType.newMember:
      requirements = [
        {
          isMandatory: true,
          isBoolean: true,
          title: "Require promotion code",
          field: "linkedEarningRules.requirePromotionCode",
          anchorKey: CAMPAIGN_EARNING_RULE_PAGE_ANCHOR_KEY.promotionCode.sectionContent.promotion_code_switch_key,
        },
        {
          isMandatory: true,
          title: "Promotion code",
          field: "linkedEarningRules.promotionCode",
          anchorKey: CAMPAIGN_EARNING_RULE_PAGE_ANCHOR_KEY.promotionCode.sectionContent.promotion_code_input_key,
        },
        {
          isMandatory: true,
          title: "Maximum number of time this code can be used",
          field: "displayPromotionCodeUsageInfo",
          anchorKey: CAMPAIGN_EARNING_RULE_PAGE_ANCHOR_KEY.promotionCode.sectionContent.promotion_code_max_times_key,
        },
      ];
      limits = [];
      break
    case EarningRuleType.memberReferral:
      requirements = [
        {
          isMandatory: true,
          title: "Beneficiary",
          field: "linkedEarningRules.referralType",
          anchorKey: CAMPAIGN_EARNING_RULE_PAGE_ANCHOR_KEY.reward.sectionContent.referral_type_key,
        },
      ];
      limits = [
        {
          isMandatory: true,
          title: "Maximum number of invitees (optional, for inviters only)",
          field: "linkedEarningRules.maxInvitees",
          anchorKey: CAMPAIGN_EARNING_RULE_PAGE_ANCHOR_KEY.limit.sectionContent.max_invitees_key,
        },
      ];
      break
    case EarningRuleType.birthday:
      requirements = [];
      limits = [];
      break
    case EarningRuleType.levelUpgrade:
      requirements = [
        {
          isMandatory: true,
          title: "Level upgrade (from, to)",
          field: "displayLevelGoal",
          errorField: "linkedEarningRules.levelGoal",
          anchorKey: CAMPAIGN_EARNING_RULE_PAGE_ANCHOR_KEY.requirements.sectionContent.level_upgrade_section_key,
        },
      ];
      limits = [
        {
          isMandatory: true,
          title: "Per head periodic Limit (optional)",
          field: "displayPerHeadPeriodicLimit",
          anchorKey: CAMPAIGN_EARNING_RULE_PAGE_ANCHOR_KEY.limit.sectionContent.level_upgrade_per_head_periodic_limit_section_key,
        },
      ];
      break
    case EarningRuleType.fillingForm:
      break
    default:
      break;
  };

  return [
    {
      step: 2,
      sectionTitle: "REQUIREMENTS",
      anchorKey: CAMPAIGN_EARNING_RULE_PAGE_ANCHOR_KEY.requirements.sectionKey,
      sectionContent: requirements,
    },
    {
      step: 2,
      sectionTitle: "REWARDS",
      anchorKey: CAMPAIGN_EARNING_RULE_PAGE_ANCHOR_KEY.reward.sectionKey,
      sectionContent: rewards,
    },
    {
      hide: [EarningRuleType.gpsCheckIn, EarningRuleType.qrCodeScanning].includes(earningRuleType),
      step: 2,
      sectionTitle: "EXECUTION PROIORITY FOR GLOBAL LIMIT",
      anchorKey: CAMPAIGN_EARNING_RULE_PAGE_ANCHOR_KEY.executionPriority.sectionKey,
      sectionContent: [
        {
          isMandatory: true,
          title: "Execution priority",
          field: "linkedEarningRules.executionPriority",
          anchorKey: CAMPAIGN_EARNING_RULE_PAGE_ANCHOR_KEY.executionPriority.sectionContent.execution_priority_input_key,
        },
      ]
    },
    {
      step: 2,
      sectionTitle: "LIMITS",
      anchorKey: CAMPAIGN_EARNING_RULE_PAGE_ANCHOR_KEY.limit.sectionKey,
      sectionContent: limits,
    },
  ]
}


const EarningRulePreviewCard = ({
  levelSource,
  errors,
  campaignData,
  language,
  hideEditButton = false,
}) => {
  const earningRule = campaignData?.linkedEarningRules || {};
  const lineItemRequirement = earningRule?.lineItemRequirement;
  const displayRewardLogicType = (
    lineItemRequirement
      ? DISPLAY_REWARD_LOGIC_TYPE?.[earningRule?.rewardLogicType] || DISPLAY_REWARD_LOGIC_TYPE[REWARD_LOGIC_TYPE.baseOnTransactionValue]
      : DISPLAY_REWARD_LOGIC_TYPE[REWARD_LOGIC_TYPE.baseOnTransactionValue]
  )

  const earningRuleData = {
    ...campaignData,
    "readableSelectedStores": (
      earningRule?.haveEligibleStores
      ? earningRule?.selectedStores?.map(item => item.displayStoreName)
      : []
    ),
    "displayRewardLogicType": displayRewardLogicType,
    "displayRewardType": DISPLAY_REWARD_TYPE?.[earningRule?.rewardType] || "",
    "displayRewardAmount": getDisplayRewardAmount(earningRule),
    displayEarningRuleAllTimeMaxAmount:
      `${earningRule?.earningRuleAllTimeLimitCoupons || '-'} coupons\n` +
      `${earningRule?.earningRuleAllTimeLimitPoints || '-'} points\n` +
      `${earningRule?.earningRuleAllTimeLimitPp || '-'} progression points\n` +
      `${earningRule?.earningRuleAllTimeLimitStamps || '-'} stamps`,
    displayEarningRuleTimePeriodPerXDay:
      earningRule?.earningRuleTimePeriodLimitPeriodicType === EARNING_RULE_PERIODIC_TYPE.perDays
        ? "Yes" : "No",
    displayEarningRuleTimePeriodSpecialPeriod:
      earningRule?.earningRuleTimePeriodLimitPeriodicType === EARNING_RULE_PERIODIC_TYPE.specificTime
        ? "Yes" : "No",
    displayEarningRuleTimePeriodFollowActivePeriod: earningRule?.earningRuleTimePeriodLimitPeriodicFollowActive
      ? "Yes" : "No",
    displayEarningRuleCustomisedTimePeriod: getReadablePeriod(
      campaignData.earningRuleTimePeriodLimitPeriodicStartTime,
      campaignData.earningRuleTimePeriodLimitPeriodicEndTime,
      campaignData.earningRuleTimePeriodLimitPeriodicFollowActive,
      "-"
    ),
    displayEarningRuleSpecialTimeMaxAmount:
      `${earningRule?.earningRuleTimePeriodLimitCoupons || '-'} coupons\n` +
      `${earningRule?.earningRuleTimePeriodLimitPoints || '-'} points\n` +
      `${earningRule?.earningRuleTimePeriodLimitPp || '-'} progression points\n` +
      `${earningRule?.earningRuleTimePeriodLimitStamps || '-'} stamps`,

    displayPerMemberAllTimeMaxAmount:
      `${earningRule?.perMemberAllTimeLimitCoupons || '-'} coupons\n` +
      `${earningRule?.perMemberAllTimeLimitPoints || '-'} points\n` +
      `${earningRule?.perMemberAllTimeLimitPp || '-'} progression points\n` +
      `${earningRule?.perMemberAllTimeLimitStamps || '-'} stamps`,
    displayPerMemberTimePeriodPerXDay:
      earningRule?.perMemberTimePeriodLimitPeriodicType === EARNING_RULE_PERIODIC_TYPE.perDays
        ? "Yes" : "No",
    displayPerMemberTimePeriodSpecialPeriod:
      earningRule?.perMemberTimePeriodLimitPeriodicType === EARNING_RULE_PERIODIC_TYPE.specificTime
        ? "Yes" : "No",
    displayPerMemberTimePeriodFollowActivePeriod: earningRule?.perMemberTimePeriodLimitPeriodicFollowActivePeriod
      ? "Yes" : "No",
    displayPerMemberCustomisedTimePeriod: getReadablePeriod(
      campaignData.perMemberTimePeriodLimitPeriodicStartTime,
      campaignData.perMemberTimePeriodLimitPeriodicEndTime,
      campaignData.perMemberTimePeriodLimitPeriodicFollowActivePeriod,
      "-"
    ),
    displayPerMemberSpecialTimeMaxAmount:
      `${earningRule?.perMemberTimePeriodLimitCoupons || '-'} coupons\n` +
      `${earningRule?.perMemberTimePeriodLimitPoints || '-'} points\n` +
      `${earningRule?.perMemberTimePeriodLimitPp || '-'} progression points\n` +
      `${earningRule?.perMemberTimePeriodLimitStamps || '-'} stamps`,
    displayPerTransactionMaxAmount:
      `${earningRule?.perTransactionLimitCoupons || '-'} coupons\n` +
      `${earningRule?.perTransactionLimitPoints || '-'} points\n` +
      `${earningRule?.perTransactionLimitPp || '-'} progression points\n` +
      `${earningRule?.perTransactionLimitStamps || '-'} stamps`,
    displayPromotionCodeUsageInfo:
      `Total times: ${earningRule?.promotionCodeUsageMaxLimit || '-'}\n` +
      `Available times: ${earningRule?.promotionCodeAvailableTimes || '-'}`,
    displayLevelGoal: getDisplayLevelGoal(levelSource, earningRule?.levelGoal),
    displayPeriodicLimit:
      earningRule?.periodicLimit && earningRule?.periodicLimitDays
        ? `${earningRule?.periodicLimit} times per ${earningRule?.periodicLimitDays} days`
        : '',
    displayPerHeadPeriodicLimit:
      earningRule?.perHeadPeriodicLimit && earningRule?.perHeadPeriodicLimitDays
        ? `${earningRule?.perHeadPeriodicLimit} times per ${earningRule?.perHeadPeriodicLimitDays} days`
        : '',
  };

  console.log("@@326", earningRuleData)

  return (
    <SinglePreviewCard
      language={language}
      data={earningRuleData}
      cardInfo={EarningRuleCard(campaignData.linkedEarningRules || {})}
      errors={errors}
      hideEditButton={hideEditButton}
    />
  )
}

const mapStateToProps = (state) => ({
  levelSource: state.levels.levelList,
  errors: state.createCampaign.errorFields,
})

export default connect(mapStateToProps)(EarningRulePreviewCard);
